// Ionic & Angular Imports
import {Component} from '@angular/core';
import {Events, NavController, Loading, LoadingController, Refresher, AlertController} from 'ionic-angular';

// Provider Imports
import {rsReportSvc, IReportDetails, ReportState} from '../../../services/report.service';
import {rsEmployeeSvc} from '../../../services/employee.service';
import {rsCompanySvc} from '../../../services/company.service';
import {rsActivitySvc } from '../../../services/activity.service';
import {rsLaborSvc} from '../../../services/labor.service';
import {rsSubcontractorSvc} from '../../../services/subcontractor.service';
import {rsPhotoSvc} from '../../../services/photo.service';
import {rsEquipmentSvc} from '../../../services/equipment.service';
import {rsEquipmentTypeSvc} from '../../../services/equipmenttype.service';
import {rsInspectionSvc } from '../../../services/inspection.service';
import {rsInspectionTypeSvc } from '../../../services/inspectiontype.service';
import {rsJurisdictionTypeSvc} from '../../../services/jurisdictiontype.service';

// Component Imports
import {BasePage} from '../../base.component';
import {ActivityPage} from './activity/activity.component';
import {LaborPage} from './labor/labor.component';
import {SubcontractorPage} from './subcontractor/subcontractor.component';
import {PicturePage} from './picture/picture.component';
import {EquipmentPage} from './equipment/equipment.component';
import {InspectionPage} from './inspection/inspection.component';
import {OrderPage} from './order/order.component';
import {NotesPage} from './notes/notes.component';
import {SchedulePage} from './schedule/schedule.component';

@Component({
    templateUrl: 'tab-main.html',
    providers : [
        rsActivitySvc,
        rsEmployeeSvc,
        rsLaborSvc,
        rsCompanySvc,
        rsSubcontractorSvc,
        rsPhotoSvc,
        rsEquipmentTypeSvc,
        rsEquipmentSvc,
        rsInspectionSvc,
        rsInspectionTypeSvc,
        rsJurisdictionTypeSvc
    ],
})
export class MainReportTab extends BasePage
{

    public state = ReportState;
    private report : IReportDetails = undefined;

    constructor(
        protected alertCtrl : AlertController,
        private reportSvc : rsReportSvc,
        private navCtrl : NavController, 
        private activitySvc: rsActivitySvc,
        private laborSvc : rsLaborSvc,
        private subcontractorSvc : rsSubcontractorSvc,
        private employeeSvc: rsEmployeeSvc,
        private companySvc : rsCompanySvc,
        private photoSvc : rsPhotoSvc,
        private equipmentSvc : rsEquipmentSvc,
        private equipmentTypeSvc : rsEquipmentTypeSvc,
        private inspectionSvc : rsInspectionSvc,
        private inspectionTypeSvc : rsInspectionTypeSvc,
        private jurisdictionTypeSvc : rsJurisdictionTypeSvc,
        private loadingCtrl : LoadingController,
        private eventSystem : Events) {

        // Construct base class
        super(alertCtrl);

        // Store a refernce to our active report for reference 
        this.report = reportSvc.getActiveReport();

        // Register our event handlers 
        this.registerEventHandlers( eventSystem );
    }

    // Class method used to subscribe to all events this class is interested in
    private registerEventHandlers( eventSystem : Events ) {
        eventSystem.subscribe( 'report:updated', (type, data) => { this.updateReportState( type, data); })
    }

    // Class method used to update the ReportState in the current view 
    private updateReportState( type: ReportState, data : any ) {
        // Update our item counters as appropriate
        switch (type) {
            case ReportState.Activity : this.report.state.activities = data; break;
            case ReportState.Labor : this.report.state.labor = data; break;
            case ReportState.Subcontractor : this.report.state.subcontractors = data; break;
            case ReportState.Equipment : this.report.state.equipment = data; break;
            case ReportState.Inspections : this.report.state.inspections = data; break;
            case ReportState.PurchaseOrders : this.report.state.purchase_orders = data; break;
            case ReportState.Pictures : this.report.state.pictures = data; break;
            case ReportState.OrdersReceived: {
                let r : IReportDetails = <IReportDetails>data;
                this.report.report.orders_received = r.report.orders_received; 
                this.report.state.orders_received = r.state.orders_received;
                break;
            }
            case ReportState.AdditionalNotes : {
                let r : IReportDetails = <IReportDetails>data;
                this.report.report.additional_notes = r.report.additional_notes;
                this.report.state.additional_notes = r.state.additional_notes;
                break;
            }
            case ReportState.ScheduleImpacts : {
                let r : IReportDetails = <IReportDetails>data;
                this.report.report.schedule_impacts = r.report.schedule_impacts;
                this.report.report.on_schedule = r.report.on_schedule;
                this.report.state.schedule_impacts = r.state.schedule_impacts;
                this.report.state.on_schedule = r.state.on_schedule;
                break;
            }
        }        
    }

    // Class method used by the template to refresh the Report
    public refreshReport( refresher : Refresher ) {
         // Spawn a spinner while the ContentList is being refreshed
        let loader : Loading = this.loadingCtrl.create( {
            content: 'Refreshing...'
        });
        loader.present().then( () => {
            // The spinner is loaded, so let's get our refreshed ContentList
            this.reportSvc.getReport(this.report.id)
            .subscribe( result => {
                // Data was received from our service 
                this.report = result;
            }, err => {
                // The operation failed, so let's handle this error 
                loader.dismiss().then( () => {
                    this.displayError( "There was an error refreshing the report.  Please try again later." ).then( () => {
                        refresher.complete();
                    });
                });
            }, () => {
                // The operation succeeded, let's dismiss our spinner and complete our refresh operation 
                loader.dismiss().then( () => {
                    refresher.complete();
                });
            });
        });       
    }

    // Class method used by the template to select a specific category of the report
    public selectCategory( type : ReportState, event : Event ) {
        // Create a loading object for use in the transition
        let loader : Loading = this.loadingCtrl.create( {
            dismissOnPageChange: true
        });

        // Set up the transition based on the Category Type
        if( type === ReportState.Activity ) {
            // Display our loader 
            loader.setContent( 'Loading...' );                
            loader.present().then( () => {
                // Retrieve our array of activities
                this.activitySvc.getActivities(this.report.id).subscribe( result => {
                    this.eventSystem.publish('report:navigate', ActivityPage, result);
                }, err => {
                    // The operation has failed; display an error message 
                    loader.dismiss().then( () => {
                        this.displayError("An unexpected error has occurred.");
                    });
                });
            }).catch( (reason) => {
                console.log('reason ', reason);
            });
        } else if( type === ReportState.Labor ) {
            // Display our loader 
            loader.setContent( 'Loading...' );                
            loader.present().then( () => {
                // Retrieve our array of laborers
                this.laborSvc.getLaborers(this.report.id).subscribe( laborResult => {
                    // Retreive our array of employees (for the scene)
                    this.employeeSvc.getEmployees().subscribe( employeeResult => {
                        this.eventSystem.publish('report:navigate', LaborPage, {
                            employees: employeeResult,
                            laborers: laborResult
                        });
                    }, employeeErr => {
                        loader.dismiss().then( () => {
                            this.displayError("An unexpected error has occurred" );
                        });
                    });
                }, err => {
                    // The operation has failed; display an error message 
                    loader.dismiss().then( () => {
                        this.displayError("An unexpected error has occurred.");
                    });
                });
            });
        } else if( type === ReportState.Subcontractor ) {
            // Display our loader 
            loader.setContent( 'Loading...' );                
            loader.present().then( () => {
                // Retrieve our array of subcontractors
                this.subcontractorSvc.getSubcontractors(this.report.id).subscribe( subResult => {
                    // Retreive our array of companies (for the scene)
                    this.companySvc.getCompanies().subscribe( companyResult => {
                        this.eventSystem.publish('report:navigate', SubcontractorPage, {
                            companies: companyResult,
                            subcontractors: subResult
                        });
                    }, companyErr => {
                        loader.dismiss().then( () => {
                            this.displayError("An unexpected error has occurred" );
                        });
                    });
                }, err => {
                    // The operation has failed; display an error message 
                    loader.dismiss().then( () => {
                        this.displayError("An unexpected error has occurred.");
                    });
                });
            });
        } else if( type === ReportState.Pictures ) {
            // Display our loader 
            loader.setContent( 'Loading...' );                
            loader.present().then( () => {
                // Retrieve our array of subcontractors
                this.photoSvc.getPhotos(this.report.id).subscribe( result => {
                    // Retrieve array of photos 
                    this.eventSystem.publish('report:navigate', PicturePage, result);
                }, err => {
                    // The operation has failed; display an error message 
                    loader.dismiss().then( () => {
                        this.displayError("An unexpected error has occurred.");
                    });
                });
            });            
        } else if( type === ReportState.Equipment ) {
            // Display our loader 
            loader.setContent( 'Loading...' );
            loader.present().then( () => {
                // Retrieve our array of subcontractors
                this.equipmentSvc.getEquipment(this.report.id).subscribe( equipResult => {
                    // Retreive our array of companies (for the scene)
                    this.equipmentTypeSvc.getEquipmentTypes().subscribe( typeResult => {
                        this.eventSystem.publish('report:navigate', EquipmentPage, {
                            equiptype: typeResult,
                            equipment: equipResult
                        });
                    }, typeErr => {
                        loader.dismiss().then( () => {
                            this.displayError("An unexpected error has occurred" );
                        });
                    });
                }, err => {
                    // The operation has failed; display an error message 
                    loader.dismiss().then( () => {
                        this.displayError("An unexpected error has occurred.");
                    });
                });
            });
        } else if( type === ReportState.Inspections ) {
            // Display our loader 
            loader.setContent( 'Loading...' );
            loader.present().then( () => {
                // Retrieve array of inspections 
                this.inspectionSvc.getInspections( this.report.id ).subscribe( inspectionResult => {
                    // Retrieve array of inspection types 
                    this.inspectionTypeSvc.getInspectionTypes().subscribe( itypeResult => {
                        // Retrieve array of jurisdiction types 
                        this.jurisdictionTypeSvc.getJurisdictionTypes().subscribe( jtypeResult => {
                            // Operation 100% complete, let's dump some data 
                            this.eventSystem.publish('report:navigate', InspectionPage, {
                                inspectionTypes: itypeResult,
                                jurisdictionTypes: jtypeResult,
                                inspections: inspectionResult
                            });                            
                        }, jtypeErr => {
                            // An error occurred retrieving jurisdiction types
                            loader.dismiss().then( () => {
                                this.displayError( "An unexected error has occurred" );
                            });
                        });
                    }, itypeErr => {
                        // An error occurred retrieving inspection types
                        loader.dismiss().then( () => {
                            this.displayError( "An unexected error has occurred" );
                        }); 
                    });
                }, err => {
                    // An error occurred retireving inspections for this report
                    loader.dismiss().then( () => {
                        this.displayError( "An unexected error has occurred" );
                    }); 
                });
            });
        } else if( type === ReportState.OrdersReceived ) {
            // Display our loader 
            loader.setContent( 'Loading...' );
            loader.present().then( () => {
                // Operation 100% complete, let's dump some data 
                this.eventSystem.publish('report:navigate', OrderPage, this.report.report.orders_received );        
            });
        } else if( type === ReportState.ScheduleImpacts ) {
            // Display our loader 
            loader.setContent( 'Loading...' );
            loader.present().then( () => {
                // Operation 100% complete, let's dump some data 
                this.eventSystem.publish('report:navigate', SchedulePage, {
                    onschedule:  this.report.report.on_schedule,
                    scheduleimpacts:  this.report.report.schedule_impacts 
                });        
            });
        } else if( type === ReportState.AdditionalNotes ) {
            // Display our loader 
            loader.setContent( 'Loading...' );
            loader.present().then( () => {
                // Operation 100% complete, let's dump some data 
                this.eventSystem.publish('report:navigate', NotesPage, this.report.report.additional_notes );        
            });
        }
    }
}


// WEBPACK FOOTER //
// ./src/pages/report/tab-main/tab-main.component.ts